import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import Chart from "react-apexcharts";
import { useTranslate } from "react-redux-multilingual";
import { jsPDF } from "jspdf";
import { IsAuthContext } from "app/App";
import { espace_mille } from "app/espace_mille";

/**
 * Palette des séries, dans un ordre FIXE : la série n prend toujours la couleur n, jamais une couleur
 * recyclée ni tirée au hasard. Remplace la palette par défaut d'ApexCharts, jugée trop criarde par
 * l'utilisateur (2026-09-29). Ces six teintes sont validées ensemble sur fond clair : bande de
 * luminosité, chroma, et surtout séparation des paires voisines pour un daltonien (ΔE 9,1 au pire,
 * seuil 8) comme pour une vision normale (19,6 au pire, seuil 15). Trois d'entre elles passent sous
 * 3:1 de contraste avec le fond blanc : c'est pourquoi la modale offre aussi une vue tableau, seule
 * façon de lire les chiffres sans dépendre de la couleur.
 *
 * Ne pas retoucher une valeur isolément : ces seuils se vérifient sur l'ensemble, pas couleur par
 * couleur. Le backend plafonne les séries à 6 (MAX_SERIES_GRAPHIQUE), d'où six entrées.
 */
const PALETTE = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300"];

/**
 * Rendus proposés dans la fenêtre, avec leur clé de libellé. Même liste que les types acceptés par
 * le backend (TYPES_GRAPHIQUE) : l'utilisateur peut passer de l'un à l'autre sans redemander à
 * l'assistant, puisque changer de rendu ne change aucun chiffre.
 */
const TYPES = [
    { valeur: "bar", cle: "chatbotIaChartTypeBar" },
    { valeur: "bar_empile", cle: "chatbotIaChartTypeBarEmpile" },
    { valeur: "bar_horizontal", cle: "chatbotIaChartTypeBarHorizontal" },
    { valeur: "line", cle: "chatbotIaChartTypeLine" },
    { valeur: "area", cle: "chatbotIaChartTypeArea" },
    { valeur: "area_empile", cle: "chatbotIaChartTypeAreaEmpile" },
    { valeur: "camembert", cle: "chatbotIaChartTypeCamembert" },
];

/** Encre des axes et légendes : du gris de texte, jamais la couleur d'une série. */
const ENCRE_SECONDAIRE = "#52514e";
/** Traits de grille et séparateurs : présents mais discrets, ils ne doivent pas concurrencer les données. */
const TRAIT_DISCRET = "#e9e9e7";

/**
 * Modale d'un graphique de vente généré par le chatbot IA (outil generer_graphique_vente).
 * Les chiffres affichés viennent tels quels du backend (StatsGestion, via ChatbotOutilsGestion) :
 * ce composant ne recalcule rien, il se contente de les tracer avec ApexCharts (même lib que les
 * autres écrans de vente, ex. ChiffreAffairesChart).
 *
 * Types de chart.type gérés : bar (groupé), bar_empile, bar_horizontal, line, area, area_empile,
 * camembert. Tous partagent la même grille catégories × séries envoyée par le backend, sauf camembert
 * (une seule période imposée côté backend, transformé ici en labels/series à plat pour ApexCharts).
 *
 * @param {Object|null} chart - { titre, type, categories, series, devise, mesure_libelle, note_prix,
 *        remarque_sections?, remarques_series? } renvoyé par le backend, ou null (modale fermée)
 * @param {Function} onClose - Ferme la modale sans effacer le lien de réouverture dans la conversation
 */
const ChatbotChartModal = ({ chart, onClose }) => {
    const t = useTranslate();
    const { authParam } = useContext(IsAuthContext);
    const chartRef = useRef(null);
    const wrapRef = useRef(null);
    // ApexCharts mesure la largeur de son conteneur à son montage et ne la re-mesure jamais ensuite.
    // Pendant l'animation d'ouverture, la modale est encore masquée : le graphique s'y dessinerait dans
    // un conteneur de 0 pixel et resterait vide. On attend donc que la modale soit réellement affichée.
    const [ouverte, setOuverte] = useState(false);
    // Génération du PDF en cours : la compression de l'image bloque le fil principal quelques
    // instants, le bouton doit dire qu'il travaille plutôt que de paraître mort.
    const [pdfEnCours, setPdfEnCours] = useState(false);
    // Vue tableau : trois des six couleurs de séries passent sous le rapport de contraste 3:1 avec le
    // fond blanc. Lire les chiffres doit donc rester possible sans distinguer les teintes — c'est aussi
    // la vue à utiliser pour relever une valeur exacte, que le graphique ne donne qu'au survol.
    const [vueTableau, setVueTableau] = useState(false);
    // Rendu et couleurs sont réglables dans la fenêtre : ni l'un ni l'autre ne touche aux chiffres,
    // qui restent ceux calculés par le serveur. Éviter ainsi de redemander un graphique à l'assistant
    // pour un simple changement d'aspect (un aller-retour de plus, et des tokens pour rien).
    const [typeCourant, setTypeCourant] = useState(null);
    const [couleurs, setCouleurs] = useState(PALETTE);
    // Étiquettes portées par le graphique : "aucune", "valeur" ou "pourcentage". null = pas encore de
    // choix de l'utilisateur, on applique alors le défaut du rendu courant (voir etiquettes).
    const [etiquettesChoisies, setEtiquettesChoisies] = useState(null);

    // Hauteur du graphique : la modale occupe 95 % de la hauteur de fenêtre (modal-95w), on laisse au
    // graphique ce qui reste une fois l'en-tête, le pied et les notes déduits. Plancher pour les très
    // petits écrans, où la modale défilera plutôt que d'écraser le graphique.
    const hauteurChart = Math.max(320, Math.round(window.innerHeight * 0.95) - 275);

    // À la fermeture, la modale disparaît d'un coup (chart repasse à null) : onExited ne se déclenche
    // pas et le drapeau resterait à true, si bien qu'à la RÉOUVERTURE le graphique se remonterait
    // aussitôt, de nouveau dans un conteneur de 0 pixel — c'était le cas « le graphique n'est vide
    // qu'à la deuxième ouverture ». On le remet donc à zéro dès qu'il n'y a plus rien à afficher.
    useEffect(() => {
        if (!chart) setOuverte(false);
    }, [chart]);

    // Un graphique rouvert repart de ce que l'assistant a demandé et de la palette d'origine, pas des
    // réglages laissés sur le graphique précédent.
    useEffect(() => {
        if (!chart) return;
        setVueTableau(false);
        setTypeCourant(chart.type);
        setCouleurs(PALETTE);
        setEtiquettesChoisies(null);
    }, [chart]);

    // Décimales des montants : même règle que les autres graphiques de vente du site
    // (ObjectifSectionChart, ChiffreAffairesChart), où elle vit déjà côté front.
    const moneyDigits = (authParam.company.advanceparams.hasOwnProperty("ventilation_espece")
        && authParam.company.advanceparams.ventilation_espece.piece.find((piece) => +piece < 1)) ? 2 : 0;

    /**
     * Formate une valeur selon ce que le serveur dit du champ tracé : décimales (null = montant, donc
     * règle de la société) et unité éventuelle (devise, %, ou rien pour un nombre de tickets).
     */
    const formatValue = useMemo(() => {
        if (!chart) return null;
        const digits = (chart.decimales === null || chart.decimales === undefined) ? moneyDigits : chart.decimales;
        const suffixe = chart.unite ? " " + chart.unite : "";
        return (val) => {
            if (val === null || val === undefined) return "-";
            // espace_mille ne sait pas formater au-delà de 999 999 999 (renvoie undefined) : un CA
            // annuel cumulé sur plusieurs sections peut les dépasser, on retombe sur la valeur brute.
            const formate = espace_mille(val, digits);
            return (formate === undefined ? val : formate) + suffixe;
        };
    }, [chart, moneyDigits]);

    // Le camembert n'a pas la même forme de données que les autres types : ApexCharts attend un tableau
    // de valeurs (series) et un tableau de noms (labels), pas la grille catégories × séries nommées.
    // Le backend garantit une seule période pour ce type (voir preparer_graphique), donc chaque série
    // ne porte qu'une seule valeur (data[0]).
    // typeCourant est posé par l'effet ci-dessus ; au tout premier rendu il vaut encore null.
    const type = typeCourant || chart?.type;
    const isPie = type === "camembert";
    // Un camembert représente une répartition à un instant donné : il n'a de sens que sur une période.
    // Même règle que le backend, rappelée ici parce que le rendu se change maintenant côté écran.
    const pieImpossible = (chart?.categories?.length ?? 0) > 1;

    const series = useMemo(() => {
        if (!chart) return null;
        return isPie ? chart.series.map((s) => s.data[0] ?? 0) : chart.series;
    }, [chart, isPie]);

    // Tant que l'utilisateur n'a rien choisi, chaque rendu garde ses étiquettes d'usage : un camembert
    // est illisible sans ses pourcentages, un histogramme à six séries est illisible avec.
    const etiquettes = etiquettesChoisies || (isPie ? "pourcentage" : "aucune");

    /**
     * Total de chaque période, toutes séries confondues. Sert aux étiquettes de total des histogrammes
     * et au calcul des pourcentages : une seule addition, pas deux façons de compter la même chose.
     */
    const totauxPeriodes = useMemo(() => {
        if (!chart || isPie) return null;
        return chart.categories.map((cat, index) =>
            chart.series.reduce((somme, serie) => somme + (serie.data[index] || 0), 0));
    }, [chart, isPie]);

    /**
     * Texte d'une étiquette posée sur le graphique. Le pourcentage se lit toujours par rapport à ce que
     * l'œil compare à cet endroit : la part du camembert, ou le total de la période pour les autres
     * rendus (la colonne, empilée ou non). Aucun chiffre n'est recalculé : ce sont les valeurs reçues.
     */
    const formatEtiquette = useMemo(() => {
        if (!formatValue) return null;
        return (val, opts) => {
            if (etiquettes === "aucune") return "";
            // Une série sans vente sur la période n'a rien à dire : son « 0 » s'empilerait avec ceux des
            // autres séries vides au ras de l'axe, illisibles et sans intérêt.
            if (!val) return "";
            if (isPie) {
                // Sur un camembert, ApexCharts passe déjà le pourcentage ; le montant, lui, se relit
                // dans la série d'origine.
                if (etiquettes === "pourcentage") return Math.round(val) + " %";
                return formatValue(opts.w.config.series[opts.seriesIndex]);
            }
            if (etiquettes === "valeur") return formatValue(val);
            const total = totauxPeriodes ? totauxPeriodes[opts.dataPointIndex] : 0;
            return total ? Math.round((val / total) * 100) + " %" : "";
        };
    }, [etiquettes, isPie, formatValue, totauxPeriodes]);

    const options = useMemo(() => {
        if (!chart) return null;

        if (isPie) {
            return {
                chart: { type: "donut", toolbar: { show: false }, fontFamily: "inherit" },
                colors: couleurs,
                labels: chart.series.map((s) => s.name),
                legend: { position: "top", horizontalAlign: "center", labels: { colors: ENCRE_SECONDAIRE } },
                tooltip: { y: { formatter: formatValue } },
                // Anneau de la couleur du fond entre deux secteurs : deux parts voisines restent
                // distinctes même quand leurs teintes se ressemblent à l'impression.
                stroke: { width: 2, colors: ["#ffffff"] },
                dataLabels: {
                    enabled: etiquettes !== "aucune",
                    formatter: formatEtiquette,
                    style: { fontWeight: 500 },
                },
                // Le total va au centre du disque, la seule place qu'un camembert réserve pour lui.
                // Toujours en valeur, comme sur les histogrammes : sur un camembert en pourcentage,
                // c'est le seul chiffre absolu lisible. Au survol d'une part, le centre affiche la
                // valeur de cette part, et revient au total ensuite.
                plotOptions: {
                    pie: {
                        donut: {
                            labels: {
                                show: etiquettes !== "aucune",
                                name: { color: ENCRE_SECONDAIRE, fontSize: "13px" },
                                value: {
                                    color: ENCRE_SECONDAIRE,
                                    fontSize: "20px",
                                    fontWeight: 600,
                                    formatter: (valeur) => formatValue(Number(valeur)),
                                },
                                total: {
                                    show: true,
                                    showAlways: true,
                                    label: chart.mesure_libelle || "",
                                    color: ENCRE_SECONDAIRE,
                                    fontSize: "13px",
                                    formatter: (w) => formatValue(w.globals.seriesTotals.reduce((somme, v) => somme + v, 0)),
                                },
                            },
                        },
                    },
                },
            };
        }

        // bar_empile et area_empile (histogramme/aires empilés) restent des charts ApexCharts de type
        // "bar"/"area" : seule l'option stacked change, ce n'est pas un type de rendu distinct côté
        // librairie. Même chose pour bar_horizontal : un histogramme groupé, juste posé sur le côté.
        const estAire = type === "area" || type === "area_empile";
        const estBarre = type !== "line" && !estAire;
        const empile = type === "bar_empile" || type === "area_empile";
        const horizontal = type === "bar_horizontal";
        // Une étiquette ne peut sortir de la barre que sur un histogramme groupé vertical : empilée,
        // elle appartient à son segment ; horizontale, il n'y a pas de « au-dessus ».
        const dansLaBarre = empile || horizontal;
        const encre = { colors: ENCRE_SECONDAIRE };
        const titreMesure = { text: chart.mesure_libelle || "", style: { color: ENCRE_SECONDAIRE, fontWeight: 500 } };
        const base = {
            chart: {
                type: type === "line" ? "line" : (estAire ? "area" : "bar"),
                stacked: empile,
                toolbar: { show: false },
                fontFamily: "inherit",
                // L'ombre portée d'ApexCharts sur les courbes fait « lourd » à l'écran et bave dans le PDF
                dropShadow: { enabled: false },
            },
            colors: couleurs,
            // Axes sans cadre ni graduations, grille horizontale seule : le regard suit les données,
            // pas le décor. Verticale retirée (elle double les catégories déjà écrites en dessous).
            grid: {
                borderColor: TRAIT_DISCRET,
                strokeDashArray: 0,
                xaxis: { lines: { show: false } },
            },
            // En barres horizontales, les axes échangent leurs rôles : les périodes passent à gauche et
            // les valeurs en bas. Le formateur doit suivre, sinon il habille les périodes en montants
            // (« 2 024 XPF » au lieu de « 2024 »), et le titre de la mesure se retrouve du mauvais côté.
            xaxis: {
                categories: chart.categories,
                axisBorder: { show: false },
                axisTicks: { show: false },
                labels: horizontal ? { formatter: formatValue, style: encre } : { style: encre },
                title: horizontal ? titreMesure : { text: "" },
            },
            yaxis: horizontal
                ? { labels: { style: encre } }
                : { title: titreMesure, labels: { formatter: formatValue, style: encre } },
            tooltip: {
                // intersect explicitement à false : ApexCharts impose intersect=true par défaut sur un
                // graphique en barres et refuse alors shared=true (erreur au rendu). Un tooltip partagé
                // est justement ce qu'on veut ici pour comparer les séries d'un histogramme groupé.
                shared: true,
                intersect: false,
                y: { formatter: formatValue },
            },
            legend: { position: "top", horizontalAlign: "center", markers: { radius: 3 }, labels: { colors: ENCRE_SECONDAIRE } },
            plotOptions: {
                bar: {
                    // Colonnes plus fines et bout arrondi côté valeur (le pied reste posé sur la ligne
                    // de base) : c'est ce qui distingue le plus un graphique récent d'un graphique daté.
                    // Pas d'arrondi sur un empilement : la version d'ApexCharts utilisée ici l'applique
                    // à CHAQUE segment, qui semble alors flotter au lieu de s'emboîter.
                    columnWidth: "45%",
                    borderRadius: empile ? 0 : 4,
                    horizontal: type === "bar_horizontal",
                    // Ancre de l'étiquette : le sommet de la barre quand elle doit s'afficher au-dessus,
                    // sans quoi l'offset la décalerait depuis le centre et elle finirait dans le vide.
                    dataLabels: { position: dansLaBarre ? "center" : "top" },
                },
            },
            // Sur une barre empilée ou horizontale, l'étiquette ne peut tenir qu'à l'intérieur de la
            // barre : elle prend le blanc par défaut d'ApexCharts, lisible sur les six teintes. Ailleurs
            // (barres groupées, courbes, aires), elle sort au-dessus de la marque et passe en gris de
            // texte — une étiquette n'est pas une donnée, elle ne porte pas la couleur de sa série.
            dataLabels: dansLaBarre
                ? { enabled: etiquettes !== "aucune", formatter: formatEtiquette, style: { fontSize: "11px", fontWeight: 500 } }
                : {
                    enabled: etiquettes !== "aucune",
                    formatter: formatEtiquette,
                    offsetY: estBarre ? -20 : -6,
                    style: { fontSize: "11px", fontWeight: 500, colors: [ENCRE_SECONDAIRE] },
                    background: { enabled: false },
                },
            // Sur un empilement, le trait blanc sépare deux segments qui se touchent ; sur une courbe,
            // c'est l'épaisseur du tracé.
            stroke: estBarre
                ? { show: empile, width: empile ? 2 : 0, colors: ["#ffffff"] }
                : { curve: "smooth", width: 2 },
        };

        // Ces deux options ne sont AJOUTÉES que si elles servent, jamais posées à undefined : ApexCharts
        // fusionne l'objet reçu avec ses valeurs par défaut, et une clé présente mais vide les écrase.
        // Un « markers: undefined » sur un histogramme lui faisait lire markers.size et planter tout le
        // composant au changement de rendu.
        if (estAire) {
            // Aires volontairement transparentes hors empilement : à pleine opacité, la série du dessus
            // masquerait purement et simplement celles du dessous.
            base.fill = { type: "solid", opacity: empile ? 0.85 : 0.2 };
        }

        // Total de chaque période, TOUJOURS en valeur — même quand les séries sont étiquetées en
        // pourcentage, où c'est le seul chiffre absolu lisible sur le graphique. Posé en annotation :
        // la version d'ApexCharts du projet n'a pas encore l'étiquette de total native des histogrammes
        // empilés (arrivée après la 3.32). Le camembert a son propre emplacement, le centre du disque.
        if (etiquettes !== "aucune") {
            // De l'air au-dessus du tracé : sans cela, le total de la période la plus haute se pose sur
            // la légende. Inutile en barres horizontales, où le total sort sur le côté.
            if (!horizontal) base.grid.padding = { top: 30 };
            base.annotations = {
                points: chart.categories.map((categorie, index) => {
                    // Empilé : la pile monte jusqu'au total. Sinon, la période culmine à sa plus haute
                    // série — l'étiquette de total surmonte alors le groupe ou le point le plus haut.
                    const sommet = empile
                        ? totauxPeriodes[index]
                        : Math.max(...chart.series.map((s) => s.data[index] || 0));
                    return {
                        // Sur des barres horizontales, les rôles des axes s'échangent : la valeur est
                        // en x, la période en y, et le total se pose au bout de la barre.
                        x: horizontal ? sommet : categorie,
                        y: horizontal ? categorie : sommet,
                        marker: { size: 0 },
                        label: {
                            text: formatValue(totauxPeriodes[index]),
                            borderWidth: 0,
                            textAnchor: horizontal ? "start" : "middle",
                            offsetX: horizontal ? 10 : 0,
                            // Collé au sommet d'une pile ; plus haut ailleurs, pour passer au-dessus des
                            // étiquettes de série qui occupent déjà le dessus des marques.
                            offsetY: horizontal ? 0 : (empile ? -4 : -24),
                            style: { background: "transparent", color: ENCRE_SECONDAIRE, fontSize: "11px", fontWeight: 700 },
                        },
                    };
                }),
            };
        }
        if (!estBarre) {
            base.markers = { size: 4, strokeWidth: 2, strokeColors: "#ffffff", hover: { size: 6 } };
        }

        return base;
    }, [chart, formatValue, isPie, type, couleurs, etiquettes, formatEtiquette, totauxPeriodes]);

    /**
     * Télécharge le graphique en PDF (A4 paysage) : l'image vient d'ApexCharts lui-même
     * (chart.dataURI), rien n'est redessiné ni recalculé ici.
     */
    const handleDownload = async () => {
        if (!chartRef.current || !chartRef.current.chart) return;
        setPdfEnCours(true);
        // Laisse le navigateur peindre l'état "en cours" avant la compression, qui, elle, monopolise
        // le fil principal : sans cette respiration le bouton ne changerait jamais d'aspect.
        await new Promise((resolve) => setTimeout(resolve, 0));

        try {
            // compress: true = compression Flate des flux du document. Indispensable ici : sans elle,
            // jsPDF décode le PNG et stocke les pixels BRUTS. Mesuré sur une image de 2150 x 976 (la
            // taille exportée ci-dessous), le même PDF pèse 8,0 Mo sans compression contre ~40 Ko avec.
            const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4", compress: true });
            const marge = 12;
            const largeur = 297 - marge * 2;
            const hauteurDispo = 210 - 26 - marge - 12; // page moins le titre, la marge basse et les notes

            // ApexCharts exporte par défaut à la taille écran (~1 px par mm une fois posé dans le PDF) :
            // l'image est alors étirée et paraît floue. On lui fait re-rasteriser son SVG au facteur qui
            // amène l'image à 200 DPI pour la largeur qu'elle occupera sur la page. Jamais en dessous de 1
            // (sur un très grand écran le rendu natif est déjà plus fin), jamais au-dessus de 4 (poids).
            const largeurCiblePx = (largeur / 25.4) * 200;
            const largeurRendue = wrapRef.current ? wrapRef.current.offsetWidth : largeurCiblePx;
            const scale = Math.max(1, Math.min(4, largeurCiblePx / largeurRendue));
            const { imgURI } = await chartRef.current.chart.dataURI({ scale });

            pdf.setFontSize(14);
            pdf.text(chart.titre || "", marge, 18);

            // Dimensions déduites des proportions réelles de l'image, pour ne pas la déformer ; un
            // graphique haut (camembert sur grand écran) est réduit pour tenir dans la page.
            const proprietes = pdf.getImageProperties(imgURI);
            let largeurImage = largeur;
            let hauteur = (proprietes.height * largeur) / proprietes.width;
            if (hauteur > hauteurDispo) {
                largeurImage = (proprietes.width * hauteurDispo) / proprietes.height;
                hauteur = hauteurDispo;
            }

            // 8e argument = compression de l'image, sans perte (contrairement à un passage en JPEG, qui
            // baverait autour du texte et des axes). "MEDIUM" (Flate niveau 6) par compromis : sur
            // l'image de test, FAST, MEDIUM et SLOW tiennent tous dans les mêmes quelques dizaines de
            // Ko, et SLOW (niveau 9 + filtre Paeth) coûterait davantage sur un graphique chargé.
            // La position en x recentre l'image quand la réduction l'a rendue plus étroite que la page.
            pdf.addImage(imgURI, "PNG", marge + (largeur - largeurImage) / 2, 26, largeurImage, hauteur, undefined, "MEDIUM");

            const notes = [chart.note_prix, chart.remarque_sections].filter(Boolean);
            if (notes.length) {
                pdf.setFontSize(9);
                pdf.text(notes, marge, Math.min(26 + hauteur + 8, 205));
            }

            const nom = (chart.titre || "graphique").normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-zA-Z0-9]+/g, "_");
            pdf.save(nom + ".pdf");
        } finally {
            // finally et pas seulement à la fin : un échec laisserait sinon le bouton désactivé à vie
            setPdfEnCours(false);
        }
    };

    if (!chart || !options) return null;

    return (
        <Modal show onHide={onClose} onEntered={() => setOuverte(true)} onExited={() => setOuverte(false)} dialogClassName="modal-95w" centered className="chatbot-ia-chart-modal">
            <Modal.Header closeButton>
                <Modal.Title>{chart.titre}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {/* Un camembert ne porte pas d'axe de catégories (une seule période, imposée par le
                    backend) : la période est rappelée ici, faute de quoi elle n'apparaîtrait nulle part. */}
                {/* Réglages d'aspect : rendu et couleurs. Masqués en vue tableau, où changer de rendu
                    remonterait le graphique alors qu'il est hors de l'écran, donc sans largeur. */}
                {!vueTableau && (
                    <div className="chatbot-ia-chart-reglages">
                        <label className="chatbot-ia-chart-reglage">
                            <span>{t("chatbotIaChartRendering")}</span>
                            <select className="form-select form-select-sm" value={type} onChange={(e) => setTypeCourant(e.target.value)}>
                                {TYPES.map((item) => (
                                    <option
                                        key={item.valeur}
                                        value={item.valeur}
                                        disabled={item.valeur === "camembert" && pieImpossible}
                                    >
                                        {t(item.cle)}
                                        {item.valeur === "camembert" && pieImpossible ? " — " + t("chatbotIaChartPieOnePeriod") : ""}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="chatbot-ia-chart-reglage">
                            <span>{t("chatbotIaChartLabels")}</span>
                            <select className="form-select form-select-sm" value={etiquettes} onChange={(e) => setEtiquettesChoisies(e.target.value)}>
                                <option value="aucune">{t("chatbotIaChartLabelsNone")}</option>
                                {/* « Valeur » et non « Montant » : la mesure tracée peut être un nombre
                                    de tickets ou un pourcentage de marge, pas seulement une somme. */}
                                <option value="valeur">{t("value")}</option>
                                <option value="pourcentage">{t("chatbotIaChartLabelsPercent")}</option>
                            </select>
                        </label>
                        <div className="chatbot-ia-chart-reglage">
                            <span>{t("couleurs")}</span>
                            <div className="chatbot-ia-chart-couleurs">
                                {chart.series.map((serie, index) => (
                                    <label key={serie.name} className="chatbot-ia-chart-couleur" title={serie.name}>
                                        <input
                                            type="color"
                                            value={couleurs[index % couleurs.length]}
                                            onChange={(e) => {
                                                const suivantes = [...couleurs];
                                                suivantes[index % couleurs.length] = e.target.value;
                                                setCouleurs(suivantes);
                                            }}
                                        />
                                        <span>{serie.name}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                        {couleurs.some((c, i) => c !== PALETTE[i]) && (
                            <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setCouleurs(PALETTE)}>
                                {t("chatbotIaChartReset")}
                            </button>
                        )}
                    </div>
                )}
                {isPie && chart.categories?.[0] && <p className="chatbot-ia-info mt-0 mb-2">{chart.categories[0]}</p>}
                {/* Un camembert est rond : étalé sur toute la largeur d'une modale plein écran, il
                    n'aurait que du vide autour de lui, à l'écran comme dans le PDF. */}
                {/* Pendant la vue tableau, le graphique est déplacé hors de l'écran — ni démonté, ni
                    caché par display:none. Démonté, il reviendrait vide (ApexCharts mesure son conteneur
                    au montage et ne le re-mesure jamais) ; en display:none, il n'a plus de dimensions et
                    l'export PDF produit une image invalide que jsPDF refuse (« type UNKNOWN »). Hors de
                    l'écran, il reste dessiné, donc téléchargeable depuis les deux vues. */}
                <div ref={wrapRef} className={[isPie ? "chatbot-ia-chart-wrap-pie" : "", vueTableau ? "chatbot-ia-chart-offscreen" : ""].filter(Boolean).join(" ")} aria-hidden={vueTableau}>
                    {/* key liée au rendu ET aux étiquettes, deux réglages qui exigent un remontage :
                        ApexCharts ne change pas de type en place de façon fiable, et react-apexcharts
                        compare les options pour décider de redessiner — or d'un choix d'étiquette à
                        l'autre, seul le formatter change, c'est-à-dire une FONCTION, que la comparaison
                        ne sait pas distinguer : le graphique restait tel quel. Les couleurs, elles, sont
                        des chaînes : elles se propagent sans remontage.
                        Sûr ici, car les réglages sont masqués en vue tableau — remonter un graphique
                        sorti de l'écran le ramènerait vide. */}
                    {ouverte && <Chart key={type + "-" + etiquettes} ref={chartRef} options={options} series={series} type={options.chart.type} height={hauteurChart} />}
                </div>
                {vueTableau && (
                    <div className="table-responsive" style={{ maxHeight: hauteurChart, overflowY: "auto" }}>
                        <table className="table table-sm table-striped mb-0">
                            <thead>
                                <tr>
                                    <th>{chart.mesure_libelle}</th>
                                    {chart.categories.map((cat) => <th key={cat} className="text-end">{cat}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {chart.series.map((serie, index) => (
                                    <tr key={serie.name}>
                                        <td>
                                            {/* La pastille rappelle la couleur de la série dans le graphique ;
                                                le nom écrit à côté porte l'information, jamais la couleur seule. */}
                                            <span className="chatbot-ia-chart-puce" style={{ backgroundColor: couleurs[index % couleurs.length] }}></span>
                                            {serie.name}
                                        </td>
                                        {serie.data.map((valeur, i) => (
                                            <td key={chart.categories[i]} className="text-end">{formatValue(valeur)}</td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {chart.note_prix && <p className="chatbot-ia-info mt-2 mb-0">{chart.note_prix}</p>}
                {chart.remarque_sections && <p className="chatbot-ia-info mb-0">{chart.remarque_sections}</p>}
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-outline-secondary btn-sm me-auto" onClick={() => setVueTableau(!vueTableau)}>
                    <i className={(vueTableau ? "i-Bar-Chart" : "i-File-Horizontal-Text") + " me-1"}></i>
                    {vueTableau ? t("chatbotIaChartView") : t("dataTable")}
                </button>
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>{t("chatbotIaClose")}</button>
                <button type="button" className="btn btn-primary btn-sm" onClick={handleDownload} disabled={pdfEnCours}>
                    {pdfEnCours
                        ? <><span className="spinner-border spinner-border-sm me-1"></span>{t("enCours")}</>
                        : <><i className="i-Download me-1"></i>{t("dlThePdf")}</>}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default ChatbotChartModal;
